import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  GitBranch, Plus, Trash2, ChevronUp, ChevronDown, Star, Archive, Check, X,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import { invalidateEnquiryStages } from "../../hooks/useEnquiryStages";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppButton, ConfirmModal,
} from "../ui";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); pipeline-settings.scss deleted
// with it. Three of its colours were literal statuses — #d97706 for the default
// star, #e11d48 for delete-hover, #059669 for restore — so none of them had a
// dark mode and none matched the pills the rest of Settings draws. They are
// --warning / --danger / --success. The two focus borders read `--accent-primary`,
// a Phase 1 alias due to go in 5.2.
//
// Row-per-stage rather than cards: the point is seeing the ORDER at a glance,
// and on the 15.6" @150% admin viewport a card grid pushes half the pipeline
// below the fold.
const ROW =
  "tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-line tw-bg-surface " +
  "tw-px-2 tw-py-1.5 max-[900px]:tw-flex-wrap";

// The two nudge arrows are generated, so the button rule is an arbitrary variant
// on their wrapper rather than a class repeated at each one.
const ORDER =
  "tw-flex tw-flex-col tw-gap-px [&_button]:tw-flex [&_button]:tw-h-3.5 [&_button]:tw-w-[18px] " +
  "[&_button]:tw-cursor-pointer [&_button]:tw-items-center [&_button]:tw-justify-center " +
  "[&_button]:tw-border-0 [&_button]:tw-bg-transparent [&_button]:tw-p-0 [&_button]:tw-leading-none " +
  "[&_button]:tw-text-content-subtle [&_button:hover:not(:disabled)]:tw-text-content " +
  "[&_button:disabled]:tw-cursor-not-allowed [&_button:disabled]:tw-opacity-30";

const NAME =
  "tw-min-w-[100px] tw-flex-1 tw-rounded-[0.375rem] tw-border tw-border-transparent " +
  "tw-bg-transparent tw-px-2 tw-py-[0.3rem] tw-font-[inherit] tw-text-[0.8125rem] tw-text-content " +
  "tw-outline-none hover:tw-border-line focus:tw-border-primary focus:tw-bg-surface";

const SEMANTIC =
  "tw-shrink-0 tw-rounded-[0.375rem] tw-border tw-border-line tw-bg-surface tw-px-[0.4rem] " +
  "tw-py-1 tw-font-[inherit] tw-text-xs tw-text-content-muted tw-outline-none";

const SWATCH_BTN =
  "tw-h-3.5 tw-w-3.5 tw-cursor-pointer tw-rounded-full tw-border-2 tw-p-0 " +
  "tw-transition-transform tw-duration-[120ms] hover:tw-scale-125";

const ICON_BTN =
  "tw-shrink-0 tw-cursor-pointer tw-rounded-[0.25rem] tw-border-0 tw-bg-transparent tw-p-1 " +
  "tw-leading-none tw-text-content-subtle hover:tw-bg-surface-hover";

const ADD_INPUT =
  "tw-max-w-[280px] tw-flex-1 tw-rounded-sm tw-border tw-border-line tw-bg-surface " +
  "tw-px-[0.65rem] tw-py-[0.45rem] tw-font-[inherit] tw-text-[0.8125rem] tw-text-content " +
  "tw-outline-none placeholder:tw-text-content-subtle focus:tw-border-primary " +
  "focus:tw-ring-[3px] focus:tw-ring-primary/[0.08]";

/**
 * Enquiry pipeline configuration.
 *
 * Institutes run different processes — a coaching centre's "Demo Done" has no
 * equivalent in a college's admissions flow — so the six statuses that used to
 * be hardcoded are now theirs to define.
 *
 * The `semantic` field is the part that is easy to skip and expensive to omit:
 * reporting needs to know which stage is a win and which is a loss without
 * guessing from the name, because "Enrolled", "Joined" and "Admitted" are all
 * the same outcome.
 */

const SEMANTICS = [
  { value: "open", label: "In progress", hint: "Still being worked" },
  { value: "won", label: "Won", hint: "Counts as a conversion" },
  { value: "lost", label: "Lost", hint: "Counts against conversion" },
];

const PRESET_COLORS = [
  // Swatches the admin picks from; the chosen value is STORED on the pipeline
  // stage and rendered back everywhere. A `hsl(var(--x))` here would be written
  // to the database and break the day the palette moved, so these stay literals.
  "#16a34a", "#2563eb", "#7c3aed", "#d97706", "#ef4444", "#0891b2", "#64748b",
];

export default function PipelineSettings() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get("/enquiry-stages", { params: { includeArchived: true } })
      .then(({ data }) => setStages(data.stages || []))
      .catch(() => showToast("Failed to load the pipeline.", "error"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  /** Any pipeline change must drop the shared cache, or other tabs stay stale. */
  const refresh = () => {
    invalidateEnquiryStages();
    load();
  };

  const patchStage = async (stage, changes) => {
    setSavingId(stage._id);
    try {
      const { data } = await apiClient.patch(`/enquiry-stages/${stage._id}`, changes);
      if (data.renamedEnquiries) {
        showToast("Stage renamed — existing enquiries were moved with it.");
      }
      refresh();
    } catch (ex) {
      showToast(ex?.response?.data?.message || "Could not update the stage.", "error");
    }
    setSavingId(null);
  };

  const addStage = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await apiClient.post("/enquiry-stages", { name });
      setNewName("");
      refresh();
    } catch (ex) {
      showToast(ex?.response?.data?.message || "Could not add the stage.", "error");
    }
    setAdding(false);
  };

  const move = async (index, direction) => {
    const next = [...stages];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setStages(next);   // optimistic — reordering should feel instant
    try {
      await apiClient.put("/enquiry-stages/reorder", { order: next.map((s) => s._id) });
      invalidateEnquiryStages();
    } catch {
      showToast("Could not save the new order.", "error");
      load();
    }
  };

  const removeStage = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { data } = await apiClient.delete(`/enquiry-stages/${confirmDelete._id}`);
      showToast(data.message);
      setConfirmDelete(null);
      refresh();
    } catch (ex) {
      showToast(ex?.response?.data?.message || "Could not remove the stage.", "error");
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Shimmer height={56} count={5} />
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <AppCard>
        <AppCardHeader>
          <AppCardTitle><GitBranch size={15} /> Enquiry pipeline</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <p className="tw-mb-3.5 tw-mt-0 tw-text-xs tw-leading-[1.5] tw-text-content-muted">
            The stages an enquiry moves through. Mark one as the default for new enquiries, and set
            whether each stage counts as won or lost so your conversion reporting is accurate.
          </p>

          <div className="tw-flex tw-flex-col tw-gap-1.5">
            {stages.map((stage, index) => (
              <div key={stage._id} className={`${ROW} ${stage.isActive ? "" : "tw-bg-surface-alt tw-opacity-[0.55]"}`}>
                <div className={ORDER}>
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">
                    <ChevronUp size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === stages.length - 1}
                    aria-label="Move down"
                  >
                    <ChevronDown size={13} />
                  </button>
                </div>

                <span className="tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-full" style={{ background: stage.color }} />

                <input
                  className={NAME}
                  defaultValue={stage.name}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && value !== stage.name) patchStage(stage, { name: value });
                  }}
                />

                <select
                  className={SEMANTIC}
                  value={stage.semantic}
                  onChange={(e) => patchStage(stage, { semantic: e.target.value })}
                >
                  {SEMANTICS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>

                <div className="tw-flex tw-shrink-0 tw-gap-[3px] max-[900px]:tw-order-10">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`${SWATCH_BTN} ${stage.color === c ? "tw-border-[var(--text-primary)]" : "tw-border-transparent"}`}
                      style={{ background: c }}
                      aria-label={`Set colour ${c}`}
                      onClick={() => patchStage(stage, { color: c })}
                    />
                  ))}
                </div>

                <span className="tw-min-w-[30px] tw-shrink-0 tw-text-right tw-text-[0.6875rem] tw-tabular-nums tw-text-content-subtle" title="Enquiries currently in this stage">
                  {stage.enquiryCount ?? 0}
                </span>

                <button
                  type="button"
                  className={`${ICON_BTN} ${stage.isDefault ? "tw-text-warning [&_svg]:tw-fill-current" : ""}`}
                  title={stage.isDefault ? "Default for new enquiries" : "Make default for new enquiries"}
                  onClick={() => !stage.isDefault && patchStage(stage, { isDefault: true })}
                >
                  <Star size={13} />
                </button>

                {stage.isActive ? (
                  <button
                    type="button"
                    className={`${ICON_BTN} hover:tw-text-danger`}
                    title="Remove or archive"
                    onClick={() => setConfirmDelete(stage)}
                  >
                    {savingId === stage._id ? <Spinner animation="border" size="sm" /> : <Trash2 size={13} />}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${ICON_BTN} tw-text-success`}
                    title="Restore this stage"
                    onClick={() => patchStage(stage, { isActive: true })}
                  >
                    <Check size={13} />
                  </button>
                )}

                {!stage.isActive && (
                  <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-[0.2rem] tw-text-[0.625rem] tw-text-content-subtle"><Archive size={11} /> Archived</span>
                )}
              </div>
            ))}
          </div>

          <div className="tw-mt-3.5 tw-flex tw-gap-2">
            <input
              className={ADD_INPUT}
              placeholder="Add a stage, e.g. Demo Done"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addStage(); }}
            />
            <AppButton size="sm" onClick={addStage} disabled={adding || !newName.trim()}>
              {adding ? <Spinner animation="border" size="sm" /> : <><Plus size={14} /> Add stage</>}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      <ConfirmModal
        show={!!confirmDelete}
        onHide={() => setConfirmDelete(null)}
        onConfirm={removeStage}
        title="Remove stage"
        message={
          confirmDelete?.enquiryCount > 0 ? (
            <>
              <strong>{confirmDelete.enquiryCount}</strong> enquiry(s) are currently in
              {" "}<strong>{confirmDelete?.name}</strong>. It will be <strong>archived</strong> rather
              than deleted, so those enquiries keep their status. Move them to another stage first if
              you want it gone entirely.
            </>
          ) : (
            <>Remove <strong>{confirmDelete?.name}</strong> from the pipeline?</>
          )
        }
        confirmText={confirmDelete?.enquiryCount > 0 ? "Archive" : "Remove"}
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
}
